import { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import MobileNav from "@/components/MobileNav";
import {
  ArrowLeft,
  Heart,
  DollarSign,
  ExternalLink,
  Copy,
  Check,
  Sparkles,
  Star
} from "lucide-react";
import { toast } from "sonner";

// Payment method icons and colors
const PAYMENT_METHODS = {
  venmo: {
    name: "Venmo",
    icon: "💙",
    color: "bg-[#008CFF]",
    hoverColor: "hover:bg-[#0074D4]",
    prefix: "https://venmo.com/",
    placeholder: "@username"
  },
  cashapp: {
    name: "Cash App",
    icon: "💚",
    color: "bg-[#00D632]",
    hoverColor: "hover:bg-[#00B82B]",
    prefix: "https://cash.app/",
    placeholder: "$cashtag"
  },
  zelle: {
    name: "Zelle",
    icon: "💜",
    color: "bg-[#6D1ED4]",
    hoverColor: "hover:bg-[#5A19B0]",
    prefix: "",
    placeholder: "email or phone"
  },
  paypal: {
    name: "PayPal",
    icon: "💛",
    color: "bg-[#003087]",
    hoverColor: "hover:bg-[#002266]",
    prefix: "https://paypal.me/",
    placeholder: "paypal.me/username"
  }
};

const TIP_AMOUNTS = [5, 10, 15, 20, 25, 50];

const TipTechPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const boardId = searchParams.get("board");
  
  const [tipLinks, setTipLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    fetchTipLinks();
  }, [boardId]);

  const fetchTipLinks = async () => {
    try {
      let response;
      if (boardId) {
        // Get tip links for the tech assigned to this board
        response = await axios.get(`${API}/board/${boardId}/tech-tip-links`, { withCredentials: true });
      } else {
        // Get venue's default tip links
        const venueRes = await axios.get(`${API}/venues`, { withCredentials: true });
        if (venueRes.data?.length > 0) {
          response = await axios.get(`${API}/venue/${venueRes.data[0].venue_id}/tip-links`, { withCredentials: true });
          // If array returned, get first one
          if (Array.isArray(response.data) && response.data.length > 0) {
            response.data = response.data[0];
          }
        }
      }
      setTipLinks(response?.data || null);
    } catch (error) {
      console.error("Error fetching tip links:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTipAmount = () => {
    if (customAmount) return parseFloat(customAmount);
    return selectedAmount;
  };

  const buildPaymentUrl = (method, handle) => {
    const amount = getTipAmount();
    const config = PAYMENT_METHODS[method];
    
    if (method === "venmo") {
      // Venmo deep link with amount
      const cleanHandle = handle.replace("@", "").replace("https://venmo.com/", "");
      if (amount) {
        return `https://venmo.com/${cleanHandle}?txn=pay&amount=${amount}&note=Thank%20you%20for%20the%20amazing%20service!`;
      }
      return `https://venmo.com/${cleanHandle}`;
    }
    
    if (method === "cashapp") {
      // Cash App link with amount
      const cleanHandle = handle.replace("$", "").replace("https://cash.app/", "");
      if (amount) {
        return `https://cash.app/$${cleanHandle}/${amount}`;
      }
      return `https://cash.app/$${cleanHandle}`;
    }
    
    if (method === "zelle") {
      // Zelle doesn't have direct links, just show the info
      return null;
    }
    
    if (method === "paypal") {
      const cleanHandle = handle.replace("https://paypal.me/", "");
      if (amount) {
        return `https://paypal.me/${cleanHandle}/${amount}`;
      }
      return `https://paypal.me/${cleanHandle}`;
    }
    
    // Custom link
    return handle;
  };

  const copyToClipboard = async (text, method) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(method);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      toast.error("Could not copy");
    }
  };

  const handlePaymentClick = (method, handle) => {
    const url = buildPaymentUrl(method, handle);
    if (url) {
      window.open(url, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-pink-300 border-t-pink-600 rounded-full animate-spin" />
      </div>
    );
  }

  const hasAnyPaymentMethod = tipLinks && (
    tipLinks.venmo || tipLinks.cashapp || tipLinks.zelle || tipLinks.paypal || tipLinks.custom_link
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white pb-32">
      {/* Header */}
      <header className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/wallet" className="p-2 -ml-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-display text-lg font-semibold">Tip Your Tech</h1>
            <div className="w-9" />
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Hero Section */}
        <div className="text-center py-6">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center">
            <Heart className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-display font-bold mb-2">
            {tipLinks?.tech_name ? `Thank ${tipLinks.tech_name}!` : "Show Your Appreciation"}
          </h2>
          <p className="text-gray-700">
            Your tip goes directly to your technician
          </p>
        </div>

        {!hasAnyPaymentMethod ? (
          /* No payment methods set up */
          <Card className="border-dashed border-2 border-gray-300">
            <CardContent className="py-8 text-center">
              <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-700 mb-2">Tip Links Not Set Up</h3>
              <p className="text-gray-600 text-sm">
                Your technician has not set up their payment links yet. 
                You can tip them in person or ask them for their Venmo/CashApp!
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Quick Amount Selection */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Select Amount
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {TIP_AMOUNTS.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => {
                        setSelectedAmount(amount);
                        setCustomAmount("");
                      }}
                      className={`py-3 px-4 rounded-xl border-2 font-semibold transition-all ${
                        selectedAmount === amount && !customAmount
                          ? "border-black bg-black text-white"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Custom:</span>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <Input
                      type="number"
                      placeholder="Other amount"
                      value={customAmount}
                      onChange={(e) => {
                        setCustomAmount(e.target.value);
                        setSelectedAmount(null);
                      }}
                      className="pl-7 rounded-xl"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  Choose Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Venmo */}
                {tipLinks.venmo && (
                  <button
                    onClick={() => handlePaymentClick("venmo", tipLinks.venmo)}
                    className="w-full flex items-center gap-3 p-4 bg-[#008CFF] hover:bg-[#0074D4] text-white rounded-xl transition-all"
                  >
                    <span className="text-2xl">💙</span>
                    <div className="flex-1 text-left">
                      <p className="font-semibold">Venmo</p>
                      <p className="text-sm opacity-80">@{tipLinks.venmo.replace("@", "").replace("https://venmo.com/", "")}</p>
                    </div>
                    <ExternalLink className="w-5 h-5 opacity-70" />
                  </button>
                )}

                {/* Cash App */}
                {tipLinks.cashapp && (
                  <button
                    onClick={() => handlePaymentClick("cashapp", tipLinks.cashapp)}
                    className="w-full flex items-center gap-3 p-4 bg-[#00D632] hover:bg-[#00B82B] text-white rounded-xl transition-all"
                  >
                    <span className="text-2xl">💚</span>
                    <div className="flex-1 text-left">
                      <p className="font-semibold">Cash App</p>
                      <p className="text-sm opacity-80">${tipLinks.cashapp.replace("$", "").replace("https://cash.app/", "")}</p>
                    </div>
                    <ExternalLink className="w-5 h-5 opacity-70" />
                  </button>
                )}

                {/* Zelle */}
                {tipLinks.zelle && (
                  <div className="w-full flex items-center gap-3 p-4 bg-[#6D1ED4] text-white rounded-xl">
                    <span className="text-2xl">💜</span>
                    <div className="flex-1 text-left">
                      <p className="font-semibold">Zelle</p>
                      <p className="text-sm opacity-80">{tipLinks.zelle}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(tipLinks.zelle, "zelle")}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      {copied === "zelle" ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <Copy className="w-5 h-5 opacity-70" />
                      )}
                    </button>
                  </div>
                )}

                {/* PayPal */}
                {tipLinks.paypal && (
                  <button
                    onClick={() => handlePaymentClick("paypal", tipLinks.paypal)}
                    className="w-full flex items-center gap-3 p-4 bg-[#003087] hover:bg-[#002266] text-white rounded-xl transition-all"
                  >
                    <span className="text-2xl">💛</span>
                    <div className="flex-1 text-left">
                      <p className="font-semibold">PayPal</p>
                      <p className="text-sm opacity-80">paypal.me/{tipLinks.paypal.replace("https://paypal.me/", "")}</p>
                    </div>
                    <ExternalLink className="w-5 h-5 opacity-70" />
                  </button>
                )}

                {/* Custom Link */}
                {tipLinks.custom_link && (
                  <button
                    onClick={() => window.open(tipLinks.custom_link, "_blank")}
                    className="w-full flex items-center gap-3 p-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all"
                  >
                    <span className="text-2xl">💵</span>
                    <div className="flex-1 text-left">
                      <p className="font-semibold">{tipLinks.custom_link_name || "Other Payment"}</p>
                      <p className="text-sm opacity-80 truncate">{tipLinks.custom_link}</p>
                    </div>
                    <ExternalLink className="w-5 h-5 opacity-70" />
                  </button>
                )}
              </CardContent>
            </Card>

            {/* Tip Message */}
            <div className="text-center py-4">
              <div className="flex items-center justify-center gap-1 text-amber-500 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 text-sm">
                Tips are 100% yours to keep. Thank you for your generosity! 
              </p>
            </div>
          </>
        )}

        {/* Back to Wallet */}
        <Link to="/wallet">
          <Button variant="outline" className="w-full rounded-xl">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Wallet
          </Button>
        </Link>
      </main>

      <MobileNav />
    </div>
  );
};

export default TipTechPage;
